import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, NavigateFunction, useLocation } from 'react-router-dom';

import { useAppDispatch } from 'app/hooks';
import AppSettingsForm from 'components/admin/AppSettingsForm';
import UsersForm from 'components/admin/UsersForm';
import Icon from 'components/icons/Icon';
import { IconSymbol } from 'components/icons/Icon.types';
import { ModalDialogLink } from 'components/modal/Modal';
import UserAvatar from 'components/user/UserAvatar';
import { UserLogoutLink } from 'components/user/UserLogoutLink';
import UserProfileForm from 'components/user/UserProfileForm';
import { SLUGS } from 'helpers/consts';
import { useAdmin } from 'selectors/adminSelectors';
import { useAppSettings } from 'selectors/appSettingsSelectors';
import { useUser } from 'selectors/userSelectors';
import DropMenu from './DropMenu';
import LocaleSelector from './LocaleSelector';
import { Logo } from './Logo';

import UserLoginForm from 'components/user/UserLoginForm';
import { sortByFareyOrder } from 'helpers/farey-sorting';
import { AnyOrderObject } from 'helpers/objects';

import { Hint } from 'components/hint/Hint';
import { IMenuItem } from 'features/menu/menuItemAPI';
import { useSite } from 'selectors/siteSelectors';
import './Navigation.scss';

var navigationTimeOut: NodeJS.Timeout | undefined = undefined;
export function navigateIn(
  path: string, // can be relative
  ms: number,
  navigate?: NavigateFunction, // use navigate if the page does not need refreshing
  beforeNavigate?: () => void,
) {
  clearTimeout(navigationTimeOut);
  navigationTimeOut = undefined;
  if (ms > 0) {
    navigationTimeOut = setTimeout(() => {
      if (beforeNavigate) beforeNavigate();
      if (navigate) navigate(path);
      else window.location.href = path;
    }, ms);
  } else {
    if (beforeNavigate) beforeNavigate();
    if (navigate) navigate(path);
    else window.location.href = path;
  }
}

export function navigateNow(
  path: string,
  navigate?: NavigateFunction,
  beforeNavigate?: () => void,
) {
  navigateIn(path, 0, navigate, beforeNavigate);
}

export function cancelNavigateIn() {
  clearTimeout(navigationTimeOut);
  navigationTimeOut = undefined;
}

type NavigationProps = {
  bg?: boolean;
  white?: boolean;
  mobile?: boolean;
};

export default function Navigation(props: NavigationProps) {
  const { bg = false, white = false, mobile = false } = props;

  const intl = useIntl();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const { user, userIsAdmin, locale } = useUser();
  const { settings, connectionError } = useAppSettings();
  const { showIds } = useAdmin();
  const [extraWeight, setExtraWeight] = useState(0);
  const userName = useMemo(() => {
    return user?.username;
  }, [user]);
  const { menus } = useSite();
  const mainMenu = useMemo(() => {
    return (
      menus?.find(
        (menu) => menu.title === 'MainMenu' && menu.locale === locale,
      ) || null
    );
  }, [menus, locale]);
  const userMenu = useMemo(() => {
    return (
      menus?.find(
        (menu) => menu.title === 'UserMenu' && menu.locale === locale,
      ) || null
    );
  }, [menus, locale]);

  function Breadcrumbs() {
    if (!user) return null;
    return <div className="nav_title"></div>;
  }

  function getNamedMenu(menu: typeof mainMenu) {
    if (!menu || !menu.items) return null;
    const mainItems = sortByFareyOrder(menu.items as AnyOrderObject[]);
    if (mobile)
      return mainItems.map((item: IMenuItem) =>
        item.slug ? (
          <div key={item.id} className="menu_item">
            <Link to={`/${item.slug}`}>{item.title}</Link>
          </div>
        ) : null,
      );
    return (
      <div
        className={classNames(menu.title?.toLowerCase() ?? '', {
          right: !userIsAdmin,
          mobile,
        })}
      >
        {mainItems.map((item: IMenuItem) =>
          item.slug ? (
            <Hint hint={item.text} key={item.id} placement="bottom">
              <div className="nav_item">
                <Link to={`/${item.slug}`}>{item.title}</Link>
              </div>
            </Hint>
          ) : null,
        )}
      </div>
    );
  }

  function NotLoggedInMenu() {
    if (user) return null;
    return (
      <>
        <div className="nav_item locale">
          <LocaleSelector locale={locale} />
        </div>
      </>
    );
  }

  function LoggedInMenu() {
    if (!user) return null;
    return (
      <>
        <div className="nav_item locale">
          <LocaleSelector locale={locale} />
        </div>
        <DropMenu className="nav_item nav_user">
          <DropMenu.Trigger>
            <UserAvatar user={user} />
            <div
              className={classNames('user_name', {
                center: user.username && user.username.length < 9,
              })}
            >
              {userName}
            </div>
          </DropMenu.Trigger>
          <DropMenu.Items>
            <ModalDialogLink
              component={UserProfileForm}
              componentProps={{ editing: true }}
            >
              <FormattedMessage id="NAVIGATION.MY_PROFILE" />
            </ModalDialogLink>
            <UserLogoutLink />
          </DropMenu.Items>
        </DropMenu>
      </>
    );
  }

  function MobileMenu() {
    return (
      <>
        <LocaleSelector locale={locale} className="nav_item locale right" />
      </>
    );
  }

  function MobileMainMenu() {
    return (
      <DropMenu className="nav_item" right>
        <DropMenu.Trigger className="nav_item">
          <Icon symbol={IconSymbol.hamburger} size={24} />
        </DropMenu.Trigger>
        <DropMenu.Items>
          {getNamedMenu(mainMenu)}
          {!user ? (
            <ModalDialogLink component={UserLoginForm}>
              <FormattedMessage id="NAVIGATION.LOGIN" />
            </ModalDialogLink>
          ) : (
            <UserLogoutLink className="menu_item" />
          )}
        </DropMenu.Items>
      </DropMenu>
    );
  }

  function AdminMenu() {
    if (!userIsAdmin) return null;
    return (
      <>
        <DropMenu className="nav_item nav_admin right">
          <DropMenu.Trigger>
            <FormattedMessage id="NAVIGATION.ADMIN" />
          </DropMenu.Trigger>
          <DropMenu.Items>
            {/* Users */}
            {userIsAdmin ? (
              <ModalDialogLink component={UsersForm}>
                <FormattedMessage id={'NAVIGATION.ADMIN_MENU.USERS'} />
              </ModalDialogLink>
            ) : null}
            {/* <DropMenu.Divider /> */}
            {/* Menu commands */}
            <Link to={`/${SLUGS.SITE_CONFIG}`}>Site Configuration</Link>
            {/* <ModalDialogLink component={CreateMenuForm}>
              <FormattedMessage id={'NAVIGATION.ADMIN_MENU.CREATE_MENU'} />
            </ModalDialogLink> */}
            {/* <DropMenu.Divider /> */}
            {/* App Settings */}
            <ModalDialogLink component={AppSettingsForm}>
              <FormattedMessage id={'NAVIGATION.ADMIN_MENU.SETTINGS'} />
            </ModalDialogLink>
            {/* <DropMenu.Divider /> */}
          </DropMenu.Items>
        </DropMenu>
      </>
    );
  }

  function AccessibilityMenu() {
    function toggleBold() {
      const newVal = extraWeight ? 0 : 300;
      setExtraWeight(newVal);
      document.documentElement.style.setProperty('--extra-weight', '' + newVal);
    }
    const cls = classNames({ extraweight: true, selected: extraWeight });
    return (
      <div onClick={toggleBold} className={cls}>
        A
      </div>
    );
  }

  function Notifications() {
    if (connectionError <= 0) return null;
    return (
      <>
        {connectionError > 1 ? <div className="notifications_overlay" /> : null}
        <div className="notifications">
          {connectionError > 0 ? (
            <h1 className="bold">
              <FormattedMessage id="NAVIGATION.ERROR_CONNECTION.TITLE" />
              &nbsp;
            </h1>
          ) : null}
          {connectionError === 1 ? (
            <div>
              <FormattedMessage id="NAVIGATION.ERROR_CONNECTION.LEVEL1" />
            </div>
          ) : connectionError > 1 ? (
            <div>
              <FormattedMessage id="NAVIGATION.ERROR_CONNECTION.LEVEL2" />
              &nbsp;
              <a href="/">
                <FormattedMessage id="NAVIGATION.ERROR_CONNECTION.RELOAD" />
              </a>
            </div>
          ) : null}
        </div>
      </>
    );
  }

  return (
    <div className="navigation_container">
      <Notifications />
      <div className={classNames('navigation', { mobile })}>
        {mobile ? <MobileMainMenu /> : null}
        <div className="nav_home">
          <Link to={'/'} title={intl.formatMessage({ id: 'NAVIGATION.HOME' })}>
            <Logo bg={bg || white} />
          </Link>
        </div>
        {mobile ? (
          <MobileMenu />
        ) : (
          <>
            {/* <Breadcrumbs /> */}
            <AdminMenu />
            {/* <AccessibilityMenu /> */}
            {/* <NamedMenu menuName="MainMenu" /> */}
            {getNamedMenu(mainMenu)}
            {/* {user ? <NamedMenu menuName="UserMenu" /> : null} */}
            {user ? getNamedMenu(userMenu) : null}
            <LoggedInMenu />
            <NotLoggedInMenu />
          </>
        )}
      </div>
    </div>
  );
}

type NavigationFooterProps = {
  mobile?: boolean;
};

export function NavigationFooter(props: NavigationFooterProps) {
  const { mobile } = props;
  const intl = useIntl();
  const { user } = useUser();

  return !mobile ? (
    <div className="navigation_footer">
      <div className="navigation">
        {!user ? (
          <ModalDialogLink className="nav_item right" component={UserLoginForm}>
            <FormattedMessage id="NAVIGATION.LOGIN" />
          </ModalDialogLink>
        ) : (
          <UserLogoutLink className="nav_item right" />
        )}
      </div>
    </div>
  ) : null;
}
